import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronLeft, LogIn } from "lucide-react";

const YASAMARU_HERO = "/yasamaru/hero.png";

export default function UnifiedLogin() {
  const nav = useNavigate();
  const { user, setSession, logout } = useAuth();
  const [form, setForm] = useState({ tenant_slug: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // If user is already logged in as super_admin and lands here, clear it so they can sign in as a tenant
  useEffect(() => {
    if (user && user.role === "super_admin") {
      logout();
    }
  }, [user, logout]);

  const update = (k) => (e) => {
    let v = e.target.value;
    if (k === "tenant_slug") v = v.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 40);
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setSession(data);
      const dest = data.user.role === "tenant_admin" ? `/t/${data.tenant.slug}/admin` : `/t/${data.tenant.slug}/punch`;
      nav(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-soft-mint relative overflow-hidden flex items-center justify-center px-4 py-12">
      <div className="blob bg-emerald-200" style={{ width: 320, height: 320, top: -40, right: -60 }} />
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800 mb-5 text-sm" data-testid="login-back-home">
          <ChevronLeft size={16} /> ホームへ戻る
        </Link>

        <div className="card-soft p-8 md:p-10">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <h1 className="font-display text-3xl font-black text-stone-900">ログイン</h1>
              <p className="text-stone-600 text-sm mt-1">顧客IDとアカウント情報でログインしてください。</p>
            </div>
            <img src={YASAMARU_HERO} alt="やさまる" className="w-16 -mt-2 hidden sm:block" />
          </div>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <Field label="顧客ID" required hint="サインアップ時に決めた英数字のID">
              <input data-testid="login-slug" required type="text" value={form.tenant_slug} onChange={update("tenant_slug")}
                placeholder="acme" className="input" autoComplete="organization"
                inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} lang="en" />
            </Field>
            <Field label="メールアドレス" required>
              <input data-testid="login-email" required type="email" value={form.email} onChange={update("email")}
                placeholder="you@example.com" className="input" autoComplete="email" />
            </Field>
            <Field label="パスワード" required>
              <input data-testid="login-password" required type="password" value={form.password} onChange={update("password")}
                placeholder="••••••••" className="input" autoComplete="current-password" />
            </Field>

            {err && <div data-testid="login-error" className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm">{err}</div>}

            <button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold py-3.5 transition-colors inline-flex items-center justify-center gap-2">
              <LogIn size={16} />{loading ? "ログイン中..." : "ログイン"}
            </button>

            <div className="text-center text-sm text-stone-500 pt-2">
              まだアカウントをお持ちでない方は{" "}
              <Link to="/signup" className="text-emerald-600 hover:text-emerald-700 font-semibold" data-testid="login-goto-signup">無料で企業登録</Link>
            </div>
          </form>
        </div>
      </div>

      <style>{`.input{ width:100%; padding:.7rem .9rem; border-radius:.75rem; border:1px solid #E7E5E4; background:#FAFAF9; outline:none;} .input:focus{ background:#fff; box-shadow:0 0 0 3px rgba(74,222,128,.25);} `}</style>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-stone-700">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      {hint && <span className="block text-xs text-stone-500 mb-1">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
